import { useState } from 'react';
import { Loader2, MessageSquareText, Phone, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  useAddCustomerPhone,
  useCustomerPhones,
  useRemoveCustomerPhone,
  useSetPhoneConsent,
  type CustomerPhone,
} from '@/hooks/useCustomerPhones';
import { Section } from './panels';

/**
 * Settings → Notifications: Text messaging (SMS) authorization.
 *
 * The phone numbers on the account — we recognize these when you call our
 * line, and (with the opt-in below) can text order updates, payment links,
 * and support replies. Consent is recorded per number with who/when/version;
 * imported numbers (from our billing systems) start NOT opted in.
 */

const CONSENT_COPY =
  'I authorize Brix Beverage to send text messages to this number for account notifications, order updates, payment links, and support. Message and data rates may apply. Reply STOP anytime to opt out.';

const SOURCE_LABEL: Record<CustomerPhone['source'], string> = {
  qbo: 'From QuickBooks',
  billandpay: 'From Bill & Pay',
  portal_user: 'Team member',
  manual: 'Added here',
  apply: 'From application',
};

function fmtPhone(p: CustomerPhone): string {
  const d = p.phone_digits ?? p.phone.replace(/\D/g, '').slice(-10);
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return p.phone;
}

export default function TextMessagingPanel() {
  const { data, isLoading } = useCustomerPhones();
  const addPhone = useAddCustomerPhone();
  const setConsent = useSetPhoneConsent();
  const removePhone = useRemoveCustomerPhone();

  const [newPhone, setNewPhone] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newConsent, setNewConsent] = useState(true);
  const [feedback, setFeedback] = useState<string | null>(null);

  const phones = data?.phones ?? [];

  return (
    <Section
      icon={<MessageSquareText size={20} />}
      title="Phone numbers & text messages"
      subtitle="The numbers on your account — we recognize them when you call, and can text you updates once you opt in."
    >
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm tracking-tight text-slate-900">
          <Loader2 size={14} className="animate-spin" /> Loading phone numbers…
        </div>
      ) : (
        <div className="space-y-4">
          {phones.length === 0 ? (
            <p className="text-sm tracking-tight text-slate-600">
              No phone numbers on file yet — add one below so we recognize you when you call.
            </p>
          ) : (
            <ul className="divide-y divide-brix-border rounded-xl border border-brix-border">
              {phones.map((p) => {
                const smsOn = p.sms_consent_at != null && p.sms_opt_out_at == null;
                return (
                  <li key={p.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <Phone size={14} className="shrink-0 text-brix-primary-deep" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold tracking-tight text-slate-900">
                        {fmtPhone(p)}
                        {p.label ? (
                          <span className="ml-2 font-normal text-slate-600">{p.label}</span>
                        ) : null}
                      </div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-500">
                        {SOURCE_LABEL[p.source] ?? p.source}
                      </div>
                    </div>
                    <label className="flex cursor-pointer items-center gap-2 text-xs tracking-tight text-slate-900">
                      <input
                        type="checkbox"
                        checked={smsOn}
                        disabled={setConsent.isPending}
                        onChange={(e) =>
                          void setConsent.mutateAsync({ id: p.id, consent: e.target.checked })
                        }
                      />
                      <span className={cn(smsOn ? 'text-emerald-700' : 'text-slate-600')}>
                        {smsOn ? 'Texts on' : 'Texts off'}
                      </span>
                    </label>
                    {(p.source === 'manual' || p.source === 'apply') && (
                      <button
                        type="button"
                        className="text-slate-400 hover:text-red-600"
                        title="Remove this number"
                        onClick={async () => {
                          try {
                            await removePhone.mutateAsync({ id: p.id });
                          } catch (err) {
                            setFeedback(err instanceof Error ? err.message : String(err));
                          }
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <form
            className="bg-brix-surface-alt/40 rounded-xl border border-brix-border px-4 py-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setFeedback(null);
              try {
                await addPhone.mutateAsync({
                  phone: newPhone.trim(),
                  label: newLabel.trim() || undefined,
                  sms_consent: newConsent,
                });
                setNewPhone('');
                setNewLabel('');
                setFeedback('Number added.');
              } catch (err) {
                setFeedback(err instanceof Error ? err.message : String(err));
              }
            }}
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Input
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="(510) 555-1234"
                required
              />
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Label (e.g. Kitchen manager cell)"
              />
            </div>
            <label className="mt-3 flex cursor-pointer items-start gap-2 text-xs leading-snug tracking-tight text-slate-700">
              <input
                type="checkbox"
                checked={newConsent}
                onChange={(e) => setNewConsent(e.target.checked)}
                className="mt-0.5"
              />
              {CONSENT_COPY}
            </label>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button type="submit" size="sm" disabled={addPhone.isPending}>
                {addPhone.isPending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Plus size={12} />
                )}
                Add number
              </Button>
              {feedback && (
                <span className="text-xs tracking-tight text-slate-600">{feedback}</span>
              )}
            </div>
          </form>
        </div>
      )}
    </Section>
  );
}
